import { createRng, pad } from './rng'
import type {
  Activity,
  Address,
  AuditMeta,
  Business,
  Department,
  DocRecord,
  Folder,
  GeoPoint,
  Grievance,
  Meeting,
  Opportunity,
  Organization,
  Project,
  RolePermissionMatrix,
  Stakeholder,
  Tag,
  TagColor,
  User,
  Role,
  Party,
  HierarchyLevel,
  PoliticalPosition,
  PoliticalUnit,
  Appointment,
  SkillNode,
  SkillAssignment,
  Intake,
  MinistryParameter,
} from '@/types'
import { ROLE_PERMISSIONS } from '@/constants/rbac'
import { DEFAULT_DISTRICTS as DISTRICTS, DISTRICT_CENTERS } from '@/constants/districts'
import { CONSTITUENCY_NAMES } from '@/constants/constituencies'

// ---------------------------------------------------------------------------
// Reference pools
// ---------------------------------------------------------------------------

const FIRST = [
  'Anirban', 'Moumita', 'Debashish', 'Sudeshna', 'Sourav', 'Ananya', 'Subhankar', 'Paromita',
  'Pradip', 'Rituparna', 'Bikash', 'Sharmila', 'Arindam', 'Mousumi', 'Tapan', 'Sohini',
  'Dipankar', 'Debjani', 'Prasenjit', 'Kakoli', 'Goutam', 'Piyali', 'Biswajit', 'Sangita',
  'Sanjib', 'Aparna', 'Ratan', 'Rima', 'Amitava', 'Nabanita',
]
const LAST = [
  'Banerjee', 'Chatterjee', 'Mukherjee', 'Bhattacharya', 'Ganguly', 'Das', 'Dutta', 'Sen',
  'Bose', 'Ghosh', 'Roy', 'Sarkar', 'Chakraborty', 'Nandi', 'Pal', 'Dey', 'Halder', 'Mondal',
  'Biswas', 'Saha', 'Dhar', 'Basu',
]
const VILLAGES = [
  'Baruipur', 'Barasat', 'Bongaon', 'Basirhat', 'Serampore', 'Chinsurah', 'Kalyani', 'Ranaghat',
  'Bidhannagar', 'Uluberia', 'Bally', 'Chandannagar', 'Diamond Harbour', 'Amtala', 'Sonarpur',
  'Rajarhat', 'Dum Dum', 'Naihati', 'Habra', 'Bhatpara',
]
const OCCUPATIONS = [
  'Farmer', 'Teacher', 'Business Owner', 'Doctor', 'Advocate', 'Social Worker', 'Contractor',
  'Shopkeeper', 'Government Employee', 'Engineer', 'Journalist', 'Retired Official', 'Trader',
  'Self-Help Group Leader', 'Panchayat Member', 'Weaver', 'Fisherman', 'Transporter',
]
const INDUSTRIES = [
  'Agro Processing', 'Textiles', 'Handloom', 'Mining', 'Food & Beverage', 'Construction',
  'Renewable Energy', 'Information Technology', 'Automotive', 'Pharmaceuticals', 'Tourism',
  'Dairy', 'Fisheries', 'Steel & Metals',
]
const DEPT_NAMES = [
  'Revenue', 'Public Works', 'Health & Family Welfare', 'Education', 'Water Resources',
  'Rural Development', 'Electricity', 'Agriculture', 'Transport', 'Social Welfare',
  'Agriculture Marketing', 'Panchayat & Rural Development',
]
/** Ministries flagged as "ours" for the Projects → Ministry Work view; everything else falls under "Others". */
const OWN_MINISTRIES = new Set(['Agriculture Marketing', 'Panchayat & Rural Development'])
const DEPT_META: Record<string, { code: string; desc: string }> = {
  'Revenue': { code: 'REV', desc: 'Land records, tax collection and revenue administration across the constituency.' },
  'Public Works': { code: 'PWD', desc: 'Roads, bridges, public buildings and civil infrastructure projects.' },
  'Health & Family Welfare': { code: 'HFW', desc: 'Primary health centres, hospitals, immunization and public health programmes.' },
  'Education': { code: 'EDU', desc: 'Schools, teacher management, scholarships and literacy initiatives.' },
  'Water Resources': { code: 'WR', desc: 'Irrigation, canals, drinking water supply and watershed management.' },
  'Rural Development': { code: 'RD', desc: 'MGNREGA, rural housing, self-help groups and village infrastructure.' },
  'Electricity': { code: 'ELEC', desc: 'Power distribution, rural electrification and grid maintenance.' },
  'Agriculture': { code: 'AGRI', desc: 'Farmer support, seeds, subsidies, soil health and crop advisories.' },
  'Transport': { code: 'TRAN', desc: 'Public transport, vehicle registration, road safety and permits.' },
  'Social Welfare': { code: 'SW', desc: 'Pensions and welfare schemes for women, children and marginalized groups.' },
  'Agriculture Marketing': { code: 'AGM', desc: 'Regulated markets, cold storage, farm-gate pricing and produce marketing infrastructure.' },
  'Panchayat & Rural Development': { code: 'PRD', desc: "Panchayati raj institutions, rural infrastructure and the ministry's own flagship schemes." },
}
const POSITION_TITLES = [
  'Secretary', 'Director', 'Deputy Secretary', 'Joint Director', 'Executive Engineer',
  'District Officer', 'Section Officer', 'Public Relations Officer', 'Accounts Officer',
]

// --- Political hierarchy reference data ---
const POL_LEVELS: HierarchyLevel[] = ['Party', 'State', 'District', 'Block', 'Gram Panchayat', 'Booth'].map(
  (name, i) => ({ id: `lvl-${i + 1}`, name, order: i }),
)
const POL_PARTIES: Party[] = [
  { id: 'pty-1', name: 'Bharatiya Janata Party', abbr: 'BJP' },
  { id: 'pty-2', name: 'All India Trinamool Congress', abbr: 'AITC/TMC' },
  { id: 'pty-3', name: 'Communist Party of India (Marxist)', abbr: 'CPI(M)' },
  { id: 'pty-4', name: 'Indian National Congress', abbr: 'INC' },
  { id: 'pty-5', name: 'All India Secular Front', abbr: 'ISF' },
]
const POL_POSITIONS: PoliticalPosition[] = [
  { id: 'pos-1', name: 'National President', levelId: 'lvl-1' },
  { id: 'pos-2', name: 'National General Secretary', levelId: 'lvl-1' },
  { id: 'pos-3', name: 'State President', levelId: 'lvl-2' },
  { id: 'pos-4', name: 'State General Secretary', levelId: 'lvl-2' },
  { id: 'pos-5', name: 'State Spokesperson', levelId: 'lvl-2' },
  { id: 'pos-6', name: 'District President', levelId: 'lvl-3' },
  { id: 'pos-7', name: 'District Secretary', levelId: 'lvl-3' },
  { id: 'pos-9', name: 'Block President', levelId: 'lvl-4' },
  { id: 'pos-10', name: 'Block Secretary', levelId: 'lvl-4' },
  { id: 'pos-11', name: 'Gram Panchayat President', levelId: 'lvl-5' },
  { id: 'pos-12', name: 'Booth President', levelId: 'lvl-6' },
  { id: 'pos-13', name: 'Booth Agent', levelId: 'lvl-6' },
  { id: 'pos-14', name: 'Treasurer' },
]
// West Bengal geography for the political hierarchy (districts → ACs / blocks)
const WB_DISTRICT_DATA: Record<string, { blocks: string[] }> = {
  'North 24 Parganas': {
    blocks: ['Barasat-I', 'Deganga', 'Habra-I', 'Amdanga', 'Gaighata', 'Barrackpore-I'],
  },
  'South 24 Parganas': {
    blocks: ['Baruipur', 'Canning-I', 'Bhangar-I', 'Sonarpur', 'Magrahat-I', 'Kulpi'],
  },
  Hooghly: {
    blocks: ['Singur', 'Polba-Dadpur', 'Chinsurah-Magra', 'Arambagh', 'Dhaniakhali', 'Balagarh'],
  },
  Nadia: {
    blocks: ['Krishnanagar-I', 'Ranaghat-I', 'Chakdaha', 'Nabadwip', 'Haringhata', 'Tehatta-I'],
  },
  'Paschim Medinipur': {
    blocks: ['Kharagpur-I', 'Medinipur Sadar', 'Ghatal', 'Garbeta-I', 'Salboni', 'Keshpur'],
  },
  'Purba Bardhaman': {
    blocks: ['Bardhaman-I', 'Katwa-I', 'Kalna-I', 'Memari-I', 'Raina-I', 'Jamalpur'],
  },
  Murshidabad: {
    blocks: ['Berhampore', 'Domkal', 'Raghunathganj-I', 'Kandi', 'Lalgola', 'Sagardighi'],
  },
  Birbhum: {
    blocks: ['Suri-I', 'Bolpur-Sriniketan', 'Rampurhat-I', 'Nalhati-I', 'Dubrajpur', 'Nanoor'],
  },
}
const WB_GPS = [
  'Ichhapur', 'Ghola', 'Bilkanda', 'Digberia', 'Chhoto Jagulia', 'Kamdebpur', 'Simulia',
  'Patharghata', 'Amtala', 'Raidighi', 'Gobardanga', 'Sahebganj', 'Bagnan', 'Panskura',
  'Kolaghat', 'Daspur', 'Haripal', 'Tarakeswar', 'Pursurah', 'Goghat',
]
const GRIEVANCE_CATS = [
  'Road & Infrastructure', 'Water Supply', 'Electricity', 'Land Records', 'Pension & Benefits',
  'Healthcare', 'Education', 'Ration & PDS', 'Employment', 'Law & Order',
]
const SKILL_CATS: Record<string, string[]> = {
  'Construction': ['Masonry', 'Carpentry', 'Plumbing', 'Electrician', 'Welding'],
  'Textiles': ['Handloom Weaving', 'Tailoring', 'Dyeing', 'Embroidery'],
  'Agriculture': ['Organic Farming', 'Beekeeping', 'Horticulture', 'Irrigation Tech'],
  'Technology': ['Data Entry', 'Web Development', 'CAD Design', 'Mobile Repair'],
  'Services': ['Beautician', 'Driver', 'Cook', 'Healthcare Assistant', 'Solar Technician'],
}
const SKILL_SUB: Record<string, string[]> = {
  Masonry: ['Brick Masonry', 'Stone Masonry'],
  'Web Development': ['Frontend', 'Backend', 'Full Stack'],
  'Handloom Weaving': ['Jamdani Weaving', 'Tant Weaving'],
  'Organic Farming': ['Vermicomposting', 'Integrated Pest Mgmt'],
}
const TAG_CATALOG: { label: string; color: TagColor }[] = [
  { label: 'VIP', color: 'orange' }, { label: 'Influencer', color: 'purple' },
  { label: 'Youth', color: 'teal' }, { label: 'Women SHG', color: 'red' },
  { label: 'Investor', color: 'green' }, { label: 'Priority', color: 'amber' },
  { label: 'Farmer Leader', color: 'green' }, { label: 'Media', color: 'blue' },
  { label: 'Follow-up', color: 'amber' }, { label: 'Grassroots', color: 'teal' },
  { label: 'Donor', color: 'green' }, { label: 'Opposition', color: 'red' },
  { label: 'Beneficiary', color: 'blue' }, { label: 'Exporter', color: 'purple' },
  { label: 'MSME', color: 'slate' }, { label: 'Startup', color: 'teal' },
]

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

export interface MockDB {
  users: User[]
  departments: Department[]
  tags: Tag[]
  stakeholders: Stakeholder[]
  businesses: Business[]
  organizations: Organization[]
  opportunities: Opportunity[]
  projects: Project[]
  grievances: Grievance[]
  meetings: Meeting[]
  documents: DocRecord[]
  folders: Folder[]
  activities: Activity[]
  rolePermissions: RolePermissionMatrix[]
  parties: Party[]
  hierarchyLevels: HierarchyLevel[]
  politicalPositions: PoliticalPosition[]
  politicalUnits: PoliticalUnit[]
  appointments: Appointment[]
  skillNodes: SkillNode[]
  skillAssignments: SkillAssignment[]
  intakes: Intake[]
  /** Editable, shared "Authority to Solve" picklist (grievances). */
  authorityList: string[]
  /** Projects → Ministry Work: named work items per ministry, each with a versioned linked PDF. */
  ministryParameters: MinistryParameter[]
}

export function buildSeed(): MockDB {
  const rng = createRng(20260716)
  const now = Date.now()
  const day = 86400000
  const iso = (offsetDays: number, hour = 10) => {
    const d = new Date(now + offsetDays * day)
    d.setHours(hour, rng.pick([0, 15, 30, 45]), 0, 0)
    return d.toISOString()
  }

  const geoNear = (district: string): GeoPoint => {
    const c = DISTRICT_CENTERS[district] ?? { lat: 22.9, lng: 88.4 }
    return { lat: Number((c.lat + rng.float(-0.2, 0.2, 4)).toFixed(4)), lng: Number((c.lng + rng.float(-0.2, 0.2, 4)).toFixed(4)) }
  }

  const address = (): Address => {
    const district = rng.pick(DISTRICTS)
    return {
      line1: `${rng.int(1, 240)}, ${rng.pick(['Rabindra Sarani', 'S. N. Banerjee Road', 'Netaji Subhas Road', 'College Street', 'Jessore Road', 'B. T. Road'])}`,
      village: rng.pick(VILLAGES),
      block: rng.pick(VILLAGES),
      district,
      state: 'West Bengal',
      pincode: `7${rng.pick([0, 0, 1, 2, 3, 4])}${pad(rng.int(1000, 9999), 4)}`,
      geo: geoNear(district),
    }
  }

  // Tags
  const tags: Tag[] = TAG_CATALOG.map((t, i) => ({ id: `tag-${pad(i + 1)}`, label: t.label, color: t.color }))

  // Authorities-to-solve seed values (grows as new labels are used/saved).
  const authorityList: string[] = [
    'Block Development Officer (BDO)', 'District Magistrate', 'Sub-Divisional Officer (SDO)',
    'Superintendent of Police (SP)', 'Municipal Commissioner', 'Panchayat Secretary',
  ]
  const someTags = (n = 2) => rng.pickMany(tags, rng.int(0, n))

  // Users
  const userSeed: { name: string; role: Role; designation: string; dept?: string }[] = [
    { name: 'Debabrata Saha', role: 'ADMIN', designation: 'Cabinet Minister' },
    { name: 'Alapan Chatterjee', role: 'CHIEF_SECRETARY', designation: 'Chief Secretary' },
    { name: 'Rituparna Sen', role: 'PERSONAL_ASSISTANT', designation: 'Personal Assistant to Minister' },
    { name: 'Debashish Das', role: 'OFFICE_STAFF', designation: 'Office Superintendent' },
    { name: 'Moumita Ghosh', role: 'OFFICE_STAFF', designation: 'Data Coordinator' },
    { name: 'Dr. Prasenjit Roy', role: 'DEPARTMENT_OFFICER', designation: 'Health Dept. Officer', dept: 'Health & Family Welfare' },
    { name: 'Kakoli Mondal', role: 'DISTRICT_COORDINATOR', designation: 'District Coordinator, Nadia', dept: 'Rural Development' },
    { name: 'Anirban Dutta', role: 'DEPARTMENT_OFFICER', designation: 'PWD Executive Engineer', dept: 'Public Works' },
    { name: 'Guest Auditor', role: 'VIEWER', designation: 'Read-only Observer' },
  ]
  const users: User[] = userSeed.map((u, i) => ({
    id: `usr-${pad(i + 1)}`,
    name: u.name,
    email: `${u.name.split(' ').slice(-1)[0].toLowerCase()}@ministeroffice.gov.in`,
    phone: `+91 9${rng.int(100000000, 999999999)}`,
    role: u.role,
    designation: u.designation,
    department: u.dept,
    district: u.role === 'DISTRICT_COORDINATOR' ? 'Nadia' : undefined,
    active: true,
    lastLoginAt: iso(-rng.int(0, 5), rng.int(8, 19)),
    permissions: ROLE_PERMISSIONS[u.role],
  }))
  const staffIds = users.map((u) => u.id)
  const audit = (): AuditMeta => ({
    createdAt: iso(-rng.int(30, 400)),
    updatedAt: iso(-rng.int(0, 25)),
    createdBy: rng.pick(staffIds),
    updatedBy: rng.pick(staffIds),
  })

  // Departments
  const departments: Department[] = DEPT_NAMES.map((n, i) => {
    const meta = DEPT_META[n]
    const stateLevel = i % 2 === 0
    return {
      id: `dep-${pad(i + 1)}`,
      code: meta.code,
      name: n,
      description: meta.desc,
      remarks: rng.bool(0.4) ? "Coordinate quarterly review with the Minister's office." : undefined,
      headOfficeAddress: stateLevel
        ? `${n} Directorate, Nabanna, Howrah - 711102`
        : `${n} District Office, ${rng.pick(DISTRICTS)} Collectorate Campus`,
      district: stateLevel ? undefined : rng.pick(DISTRICTS),
      parentDepartmentId: i > 6 && i < 10 ? 'dep-002' : undefined,
      displayOrder: i,
      active: true,
      isOwnMinistry: OWN_MINISTRIES.has(n) || undefined,
      head: `${rng.pick(FIRST)} ${rng.pick(LAST)}`,
      positions: rng.pickMany(POSITION_TITLES, rng.int(2, 4)).map((title, k) => ({
        id: `dpos-${i}-${k}`,
        title,
        contactId: rng.bool(0.7) ? `stk-${pad(rng.int(1, 64))}` : undefined,
      })),
      followups: Array.from({ length: rng.int(0, 3) }, (_, k) => ({
        id: `depf-${i}-${k}`,
        dueDate: iso(rng.int(-8, 20)),
        note: rng.pick(['Submit utilization certificate', 'Prepare progress report', 'Schedule field inspection', 'Reconcile beneficiary list']),
        status: rng.pick(['open', 'open', 'done'] as const),
        createdAt: iso(-rng.int(1, 30)),
      })),
      notes: Array.from({ length: rng.int(0, 2) }, (_, k) => ({
        id: `depn-${i}-${k}`,
        title: rng.bool(0.5) ? 'Review note' : undefined,
        body: rng.pick(['Pending clarifications on last sanction order.', 'Good progress on flagship scheme this quarter.', 'Awaiting fund release from finance.']),
        createdAt: iso(-rng.int(1, 40)),
        authorId: rng.pick(staffIds),
      })),
      audit: audit(),
    }
  })

  // Organizations
  const organizations: Organization[] = Array.from({ length: 12 }, (_, i) => ({
    id: `org-${pad(i + 1)}`,
    name: `${rng.pick(VILLAGES)} ${rng.pick(['Farmers', 'Weavers', 'Women', 'Youth', 'Traders'])} ${rng.pick(['Cooperative', 'Association', 'Union', 'Society', 'Federation'])}`,
    type: rng.pick(['ngo', 'cooperative', 'association', 'union', 'institution', 'government']),
    memberCount: rng.int(25, 1200),
    address: address(),
    tags: someTags(),
    audit: audit(),
  }))

  // Businesses
  const businesses: Business[] = Array.from({ length: 30 }, (_, i) => {
    const industry = rng.pick(INDUSTRIES)
    const turnover = rng.int(5, 900) * 100000
    return {
      id: `biz-${pad(i + 1)}`,
      name: `${rng.pick(['Maa Tara', 'Bengal', 'Ganga', 'Hooghly', 'Sundarban', 'Netaji', 'Eastern', 'Kolkata'])} ${industry.split(' ')[0]} ${rng.pick(['Industries', 'Enterprises', 'Pvt Ltd', 'Udyog', 'Agro', 'Works'])}`,
      industry,
      sector: rng.pick(['manufacturing', 'services', 'agriculture', 'trade', 'technology', 'other']),
      products: rng.pickMany(['Rice', 'Turmeric', 'Handloom Sarees', 'Steel Rods', 'Solar Panels', 'Packaged Snacks', 'Furniture', 'Bricks'], rng.int(1, 3)),
      services: rng.pickMany(['Processing', 'Logistics', 'Contract Manufacturing', 'Export', 'Retail'], rng.int(0, 2)),
      turnover: { amount: turnover, currency: 'INR' },
      employees: rng.int(5, 450),
      machinery: rng.pickMany(['CNC Lathe', 'Power Loom', 'Boiler', 'Packaging Unit', 'Cold Storage', 'Solar Array'], rng.int(1, 3)),
      certifications: rng.pickMany(['ISO 9001', 'FSSAI', 'GMP', 'BIS', 'Udyam Registered'], rng.int(1, 3)),
      manufacturingCapacity: `${rng.int(50, 5000)} units/month`,
      exports: rng.bool(0.35),
      governmentSchemes: rng.pickMany(['PMEGP', 'MUDRA', 'Startup Bengal', 'CGTMSE', 'ODOP', 'Banglashree'], rng.int(0, 2)),
      skillIds: [],
      address: address(),
      tags: someTags(),
      establishedYear: rng.int(1998, 2024),
      status: rng.pick(['active', 'active', 'active', 'inactive', 'prospect']),
      audit: audit(),
    }
  })

  // Stakeholders
  const stakeholders: Stakeholder[] = Array.from({ length: 64 }, (_, i) => {
    const addr = address()
    const sentiment = rng.pick(['supporter', 'supporter', 'neutral', 'opposition', 'undecided'] as const)
    return {
      id: `stk-${pad(i + 1)}`,
      firstName: rng.pick(FIRST),
      lastName: rng.pick(LAST),
      gender: rng.pick(['male', 'female', 'other']),
      dob: iso(-rng.int(8000, 24000)),
      occupation: rng.pick(OCCUPATIONS),
      constituency: rng.pick(CONSTITUENCY_NAMES),
      address: addr,
      phone: `+91 9${rng.int(100000000, 999999999)}`,
      email: rng.bool(0.5) ? `${rng.pick(FIRST).toLowerCase()}.${rng.pick(LAST).toLowerCase()}@gmail.com` : undefined,
      organizationId: rng.bool(0.4) ? rng.pick(organizations).id : undefined,
      businessIds: rng.bool(0.3) ? [rng.pick(businesses).id] : [],
      skillIds: [],
      family: Array.from({ length: rng.int(0, 3) }, () => ({
        name: `${rng.pick(FIRST)} ${rng.pick(LAST)}`,
        relation: rng.pick(['Spouse', 'Son', 'Daughter', 'Father', 'Brother']),
        occupation: rng.pick(OCCUPATIONS),
      })),
      social: {
        twitter: rng.bool(0.3) ? '@' + rng.pick(FIRST).toLowerCase() : undefined,
        facebook: rng.bool(0.4) ? 'fb.com/' + rng.pick(FIRST).toLowerCase() : undefined,
      },
      tags: someTags(3),
      relationshipStatus: rng.pick(['strong', 'moderate', 'weak', 'new']),
      sentiment,
      contactPreference: rng.pick(['phone', 'whatsapp', 'email', 'letter', 'in_person']),
      vip: rng.bool(0.15),
      audit: audit(),
    }
  })

  // Opportunities
  const opportunities: Opportunity[] = Array.from({ length: 24 }, (_, i) => {
    const stage = rng.pick(['not_started', 'not_started', 'in_progress', 'in_progress', 'in_progress', 'completed', 'cancelled'] as const)
    return {
      id: `opp-${pad(i + 1)}`,
      title: `${rng.pick(['Solar Park', 'Food Processing Cluster', 'Handloom Hub', 'Cold Storage Chain', 'Skill Center', 'Agro Mandi', 'IT Park', 'Mineral Beneficiation'])} — ${rng.pick(DISTRICTS)}`,
      sector: rng.pick(INDUSTRIES),
      description: 'Proposed investment to boost local employment and value addition within the constituency.',
      investment: { amount: rng.int(2, 500) * 1000000, currency: 'INR' },
      location: address(),
      stage,
      priority: rng.pick(['low', 'medium', 'high', 'urgent']),
      requiredGovtSupport: rng.pickMany(['Land Allotment', 'Power Connection', 'Single-window Clearance', 'Subsidy', 'Skill Training'], rng.int(1, 3)),
      relatedDepartments: rng.pickMany(DEPT_NAMES, rng.int(1, 3)),
      expectedOutcomes: ['Local employment generation', 'Increased tax revenue', 'Infrastructure upgrade'],
      stakeholderIds: rng.pickMany(stakeholders.map((s) => s.id), rng.int(1, 4)),
      // Hard "act-by" deadline — a spread of overdue, imminent, and comfortable windows.
      actByDate: iso(rng.int(-12, 75)),
      audit: audit(),
    }
  })

  // Projects
  const projects: Project[] = Array.from({ length: 16 }, (_, i) => {
    const budget = rng.int(20, 800) * 100000
    const progress = rng.int(0, 100)
    return {
      id: `prj-${pad(i + 1)}`,
      name: `${rng.pick(['Rural Road', 'Piped Water', 'PHC Upgrade', 'School Building', 'Micro-Irrigation', 'Street Lighting', 'Community Hall'])} — ${rng.pick(VILLAGES)}`,
      opportunityId: rng.bool(0.4) ? rng.pick(opportunities).id : undefined,
      department: rng.pick(DEPT_NAMES),
      budget: { amount: budget, currency: 'INR' },
      spent: { amount: Math.round((budget * progress) / 100), currency: 'INR' },
      status: rng.pick(['planning', 'in_progress', 'in_progress', 'on_hold', 'completed']),
      progress,
      startDate: iso(-rng.int(30, 300)),
      location: address(),
      beneficiaries: rng.int(200, 25000),
      audit: audit(),
    }
  })

  // Issues (internally still "grievances") — reference ISS-YYYYMMDD-NNN, counter resets per day
  const taskDailyCounter: Record<string, number> = {}
  const grievances: Grievance[] = Array.from({ length: 52 }, (_, i) => {
    const status = rng.pick(['registered', 'verification', 'assigned', 'in_progress', 'in_progress', 'resolved', 'resolved', 'closed'] as const)
    const citizen = rng.pick(stakeholders)
    // Some issues are filed jointly by more than one citizen.
    const coCitizens = rng.bool(0.3) ? [rng.pick(stakeholders)].filter((c) => c.id !== citizen.id) : []
    const citizens = [citizen, ...coCitizens].map((c) => ({ id: c.id, name: `${c.firstName} ${c.lastName}`, phone: c.phone }))
    // Escalation is an independent dimension — open cases may be flagged for attention.
    const escalationLevel = status !== 'resolved' && status !== 'closed' && rng.bool(0.2) ? rng.int(1, 2) : 0
    const meta = audit()
    const created = new Date(meta.createdAt)
    const stamp = `${created.getFullYear()}${pad(created.getMonth() + 1, 2)}${pad(created.getDate(), 2)}`
    const seq = (taskDailyCounter[stamp] = (taskDailyCounter[stamp] ?? 0) + 1)
    return {
      id: `grv-${pad(i + 1)}`,
      reference: `ISS-${stamp}-${pad(seq)}`,
      title: `${rng.pick(GRIEVANCE_CATS)} issue at ${rng.pick(VILLAGES)}`,
      description: 'Citizen reports a pending civic issue requiring departmental action and timely resolution.',
      category: rng.pick(GRIEVANCE_CATS),
      citizenId: citizen.id,
      citizenName: `${citizen.firstName} ${citizen.lastName}`,
      citizenPhone: citizen.phone,
      citizens,
      affectedParties: citizens.map((c) => ({
        label: rng.pick(['Affected resident', 'Complainant', 'Land owner', 'Local resident', 'Village committee']),
        contactId: c.id,
        name: c.name,
        phone: c.phone,
      })),
      authorities: rng.bool(0.7)
        ? [{ label: rng.pick(['Block Development Officer', 'Sub-Divisional Officer', 'Panchayat Pradhan', 'Junior Engineer', 'District Magistrate Office']) }]
        : [],
      department: rng.pick(DEPT_NAMES),
      priority: rng.pick(['low', 'medium', 'high', 'urgent']),
      status,
      location: citizen.address,
      escalationLevel,
      escalationHistory: escalationLevel > 0 ? [{ id: 'e1', at: iso(-rng.int(1, 10)), fromLevel: 0, toLevel: escalationLevel, reason: 'Pending beyond threshold', byUserId: rng.pick(staffIds) }] : [],
      resolutionNote: status === 'resolved' || status === 'closed' ? 'Issue addressed and confirmed with the citizen.' : undefined,
      resolvedAt: status === 'resolved' || status === 'closed' ? iso(-rng.int(1, 15)) : undefined,
      tags: someTags(),
      audit: meta,
    }
  })

  // Meetings — spread around today
  const meetings: Meeting[] = Array.from({ length: 46 }, (_, i) => {
    const offset = rng.int(-20, 30)
    const startHour = rng.int(9, 17)
    const start = new Date(now + offset * day)
    start.setHours(startHour, rng.pick([0, 30]), 0, 0)
    const end = new Date(start.getTime() + rng.int(30, 120) * 60000)
    const type = rng.pick(['constituency', 'departmental', 'business', 'grievance', 'public', 'internal', 'protocol'] as const)
    const status = offset < 0 ? rng.pick(['completed', 'completed', 'cancelled'] as const) : rng.pick(['scheduled', 'confirmed'] as const)
    return {
      id: `mtg-${pad(i + 1)}`,
      title: `${rng.pick(['Review', 'Delegation', 'Public Hearing', 'Inauguration', 'Coordination', 'Grievance Camp', 'Investor'])} — ${rng.pick(['Water Supply', 'Road Project', 'Health Camp', 'Skill Mission', 'Industry', 'Panchayat'])}`,
      type,
      status,
      start: start.toISOString(),
      end: end.toISOString(),
      location: `${rng.pick(['Collectorate', 'Circuit House', 'Community Hall', 'Minister\'s Camp Office', 'Block Office'])}, ${rng.pick(DISTRICTS)}`,
      geo: geoNear(rng.pick(DISTRICTS)),
      virtual: rng.bool(0.2),
      agenda: rng.pickMany(['Progress review', 'Budget discussion', 'Public feedback', 'Approval of proposals', 'Grievance redressal', 'Site inspection plan'], rng.int(2, 4)),
      participants: rng.pickMany(stakeholders, rng.int(2, 6)).map((s) => ({ id: s.id, name: `${s.firstName} ${s.lastName}`, type: 'stakeholder' as const, refId: s.id, attendance: offset < 0 ? rng.pick(['attended', 'absent']) : 'invited' })),
      minutes: status === 'completed' ? 'Discussion concluded with agreed action points and follow-up owners assigned.' : undefined,
      actionItems: status === 'completed' ? Array.from({ length: rng.int(1, 3) }, (_, k) => ({ id: `ai-${i}-${k}`, text: rng.pick(['Send follow-up letter', 'Prepare cost estimate', 'Schedule site visit', 'Coordinate with PWD']), done: rng.bool() })) : [],
      linkedGrievanceIds: rng.bool(0.3) ? [rng.pick(grievances).id] : [],
      linkedOpportunityIds: rng.bool(0.25) ? [rng.pick(opportunities).id] : [],
      travelTimeMins: rng.bool(0.5) ? rng.int(15, 120) : undefined,
      recurrence: rng.pick(['none', 'none', 'none', 'weekly', 'monthly']),
      color: rng.pick(['blue', 'teal', 'orange', 'green', 'purple', 'amber']),
      tags: someTags(),
      outcome: status === 'completed' ? rng.pick(['Successful', 'Follow-up required', 'Partially resolved']) : undefined,
      audit: audit(),
    }
  })

  // Documents
  const folders: Folder[] = [
    { id: 'fld-01', name: 'Grievances' },
    { id: 'fld-02', name: 'Opportunities' },
    { id: 'fld-03', name: 'Meeting Minutes' },
    { id: 'fld-04', name: 'Legal & Land' },
    { id: 'fld-05', name: 'Reports' },
  ]
  const documents: DocRecord[] = Array.from({ length: 34 }, (_, i) => {
    const type = rng.pick(['pdf', 'image', 'doc', 'sheet', 'slide', 'other'] as const)
    const size = rng.int(40, 8000) * 1024
    return {
      id: `doc-${pad(i + 1)}`,
      name: `${rng.pick(['Sanction Order', 'Site Photo', 'Meeting Minutes', 'Land Record', 'Status Report', 'Proposal', 'Petition', 'Estimate'])} ${pad(i + 1)}.${type === 'image' ? 'jpg' : type === 'sheet' ? 'xlsx' : type === 'doc' ? 'docx' : 'pdf'}`,
      type,
      category: rng.pick(['Legal', 'Financial', 'Correspondence', 'Technical', 'Media']),
      folderId: rng.pick(folders).id,
      size,
      ocrText: type === 'image' || type === 'pdf' ? 'Scanned document content indexed for search.' : undefined,
      linkedEntities: rng.bool(0.6) ? [rng.pick([...grievances.map((g) => ({ type: 'grievance' as const, id: g.id, label: g.reference })), ...opportunities.map((o) => ({ type: 'opportunity' as const, id: o.id, label: o.title }))])] : [],
      tags: someTags(),
      audit: audit(),
    }
  })

  // Activities — generate per key entity
  const activities: Activity[] = []
  let actId = 0
  const addActivity = (a: Omit<Activity, 'id'>) => activities.push({ ...a, id: `act-${pad(++actId, 4)}` })
  stakeholders.forEach((s) => {
    const count = rng.int(3, 8)
    for (let k = 0; k < count; k++) {
      const kind = rng.pick(['note', 'meeting', 'letter', 'status_change', 'grievance', 'task'] as const)
      const actor = rng.pick(users)
      addActivity({
        entity: { type: 'stakeholder', id: s.id, label: `${s.firstName} ${s.lastName}` },
        kind,
        title: {
          note: 'Note added', meeting: 'Met at constituency office', letter: 'Official letter dispatched',
          status_change: 'Relationship status updated', grievance: 'Linked grievance registered', task: 'Follow-up task created',
        }[kind] ?? 'Activity',
        body: rng.pick(['Discussed pending water project.', 'Requested support for upcoming event.', 'Shared issue status update.', 'Confirmed attendance for meeting.', undefined as unknown as string]),
        at: iso(-rng.int(0, 120), rng.int(8, 20)),
        actorId: actor.id,
        actorName: actor.name,
      })
    }
  })
  grievances.forEach((g) => {
    addActivity({ entity: { type: 'grievance', id: g.id, label: g.reference }, kind: 'grievance', title: 'Grievance registered', body: g.title, at: g.audit.createdAt, actorId: g.audit.createdBy, actorName: users.find((u) => u.id === g.audit.createdBy)?.name ?? 'System' })
    if (g.status === 'resolved' || g.status === 'closed')
      addActivity({ entity: { type: 'grievance', id: g.id, label: g.reference }, kind: 'status_change', title: 'Marked resolved', body: g.resolutionNote, at: g.resolvedAt!, actorId: rng.pick(staffIds), actorName: rng.pick(users).name })
  })
  activities.sort((a, b) => (a.at < b.at ? 1 : -1))

  // Notifications
  const rolePermissions: RolePermissionMatrix[] = (Object.keys(ROLE_PERMISSIONS) as Role[]).map((role) => ({
    role,
    permissions: ROLE_PERMISSIONS[role],
  }))

  // Political hierarchy — build a unit tree per party + office-bearer appointments
  const politicalUnits: PoliticalUnit[] = []
  const appointments: Appointment[] = []
  let unitSeq = 0
  let apptSeq = 0
  const mkUnit = (partyId: string, name: string, levelId: string, parentId?: string): PoliticalUnit => {
    const u: PoliticalUnit = { id: `unit-${pad(++unitSeq)}`, partyId, name, levelId, parentId, audit: audit() }
    politicalUnits.push(u)
    return u
  }
  const mkAppointments = (unit: PoliticalUnit, levelId: string) => {
    const eligible = POL_POSITIONS.filter((p) => p.levelId === levelId || !p.levelId)
    const usedPositions = new Set<string>()
    const count = rng.int(0, 3)
    for (let k = 0; k < count; k++) {
      const pos = rng.pick(eligible)
      if (usedPositions.has(pos.id)) continue
      usedPositions.add(pos.id)
      const ended = rng.bool(0.25)
      appointments.push({
        id: `appt-${pad(++apptSeq, 4)}`,
        unitId: unit.id,
        positionId: pos.id,
        contactId: `stk-${pad(rng.int(1, 64))}`,
        startDate: iso(-rng.int(60, 900)),
        endDate: ended ? iso(-rng.int(1, 59)) : undefined,
      })
    }
  }
  const WB_DISTRICT_NAMES = Object.keys(WB_DISTRICT_DATA)
  POL_PARTIES.forEach((party) => {
    // Top-level unit IS the party (Party-level node = the party itself)
    const partyUnit = mkUnit(party.id, party.name, 'lvl-1')
    mkAppointments(partyUnit, 'lvl-1')
    const state = mkUnit(party.id, `West Bengal State Committee (${party.abbr})`, 'lvl-2', partyUnit.id)
    mkAppointments(state, 'lvl-2')
    rng.pickMany(WB_DISTRICT_NAMES, 3).forEach((d) => {
      const data = WB_DISTRICT_DATA[d]
      const dist = mkUnit(party.id, `${d} District Committee`, 'lvl-3', state.id)
      mkAppointments(dist, 'lvl-3')
      rng.pickMany(data.blocks, 2).forEach((blk) => {
        const block = mkUnit(party.id, `${blk} Block`, 'lvl-4', dist.id)
        mkAppointments(block, 'lvl-4')
        const gp = mkUnit(party.id, `${rng.pick(WB_GPS)} Gram Panchayat`, 'lvl-5', block.id)
        mkAppointments(gp, 'lvl-5')
        const booth = mkUnit(party.id, `Booth #${rng.int(1, 295)} — ${rng.pick(WB_GPS)}`, 'lvl-6', gp.id)
        mkAppointments(booth, 'lvl-6')
      })
    })
  })

  // Skill taxonomy tree + contact assignments
  const skillNodes: SkillNode[] = []
  const skillAssignments: SkillAssignment[] = []
  let sknSeq = 0
  let skaSeq = 0
  const mkSkill = (name: string, parentId?: string): SkillNode => {
    const n: SkillNode = { id: `skn-${pad(++sknSeq)}`, name, parentId }
    skillNodes.push(n)
    return n
  }
  const assignSkill = (node: SkillNode, min: number, max: number) => {
    const used = new Set<string>()
    const count = rng.int(min, max)
    for (let k = 0; k < count; k++) {
      const cid = `stk-${pad(rng.int(1, 64))}`
      if (used.has(cid)) continue
      used.add(cid)
      skillAssignments.push({
        id: `ska-${pad(++skaSeq, 4)}`,
        skillNodeId: node.id,
        contactId: cid,
        level: rng.pick(['beginner', 'intermediate', 'advanced', 'expert'] as const),
        assignedAt: iso(-rng.int(1, 400)),
      })
    }
  }
  Object.entries(SKILL_CATS).forEach(([cat, skills]) => {
    const catNode = mkSkill(cat)
    assignSkill(catNode, 0, 2)
    skills.forEach((sk) => {
      const skNode = mkSkill(sk, catNode.id)
      const subs = SKILL_SUB[sk]
      if (subs) {
        assignSkill(skNode, 0, 2)
        subs.forEach((sub) => assignSkill(mkSkill(sub, skNode.id), 1, 4))
      } else {
        assignSkill(skNode, 1, 5)
      }
    })
  })

  // Front Desk — the triage/intake entry point for the back office
  const INTAKE_SUBJECTS = [
    'Caller reports broken street light on the main road',
    'Request to meet the Minister about school funding',
    'Proposal for a new cold-storage unit near the market',
    'Complaint about irregular water supply in the ward',
    'Invitation to inaugurate a community health camp',
    'Land dispute — caller seeking the Minister’s intervention',
    'Query about pension amount not credited',
    'Local entrepreneur wants to discuss investment support',
    'SMS: pothole causing accidents near the bus stand',
  ]
  const INTAKE_CHANNELS: Intake['channel'][] = ['call', 'sms', 'whatsapp', 'email', 'letter', 'walk_in', 'other']
  const intakes: Intake[] = Array.from({ length: 9 }, (_, i) => {
    const person = rng.pick(stakeholders)
    const meta = audit()
    const created = new Date(meta.createdAt)
    const stamp = `${created.getFullYear()}${pad(created.getMonth() + 1, 2)}${pad(created.getDate(), 2)}`
    let status: Intake['status'] = 'new'
    let classifiedAs: Intake['classifiedAs']
    let linkedRef: string | undefined
    let linkedTo: string | undefined
    if (rng.bool(0.4)) {
      const kind = rng.pick(['grievance', 'opportunity', 'meeting'] as const)
      status = 'classified'
      classifiedAs = kind
      if (kind === 'grievance') { const g = rng.pick(grievances); linkedRef = g.reference; linkedTo = `/issues/${g.id}` }
      else if (kind === 'opportunity') { const o = rng.pick(opportunities); linkedRef = o.title; linkedTo = `/opportunities/${o.id}` }
      else { const m = rng.pick(meetings); linkedRef = m.title; linkedTo = `/meetings/${m.id}` }
    }
    return {
      id: `itk-${pad(i + 1)}`,
      reference: `FD-${stamp}-${pad((i % 9) + 1)}`,
      subject: rng.pick(INTAKE_SUBJECTS),
      channel: rng.pick(INTAKE_CHANNELS),
      fromName: `${person.firstName} ${person.lastName}`,
      fromPhone: person.phone,
      contactId: rng.bool(0.7) ? person.id : undefined,
      note: 'Logged at the back office; awaiting triage and classification.',
      receivedAt: meta.createdAt,
      handlerId: rng.pick(staffIds),
      priority: rng.pick(['low', 'medium', 'high', 'urgent'] as const),
      status,
      classifiedAs,
      linkedRef,
      linkedTo,
      audit: meta,
    }
  })

  // Ministry Work: a couple of seeded parameters under the two "own" ministries.
  const deptIdByName = new Map(departments.map((d) => [d.name, d.id]))
  const parameterSeed: { dept: string; name: string; description: string; doc: string }[] = [
    {
      dept: 'Agriculture Marketing',
      name: 'WBCADC — Mrittwika',
      description: 'West Bengal Comprehensive Area Development Corporation scheme, marketed under the Mrittwika brand.',
      doc: 'WBCADC_Mrittwika_Sanction_Order.pdf',
    },
    {
      dept: 'Panchayat & Rural Development',
      name: 'Rural Connectivity Scheme',
      description: 'Panchayat-level road and drainage connectivity programme.',
      doc: 'Rural_Connectivity_Guidelines.pdf',
    },
  ]
  const ministryParameters: MinistryParameter[] = parameterSeed.map((p, i) => {
    const meta = audit()
    return {
      id: `mpar-${pad(i + 1)}`,
      departmentId: deptIdByName.get(p.dept) ?? '',
      name: p.name,
      description: p.description,
      documents: [
        { id: `mpdoc-${i + 1}-1`, name: p.doc, uploadedAt: meta.createdAt, uploadedBy: meta.createdBy },
      ],
      audit: meta,
    }
  })

  return {
    users, departments, tags, stakeholders, businesses, organizations,
    opportunities, projects, grievances, meetings, documents, folders,
    activities, rolePermissions,
    parties: POL_PARTIES, hierarchyLevels: POL_LEVELS, politicalPositions: POL_POSITIONS,
    politicalUnits, appointments,
    skillNodes, skillAssignments,
    intakes,
    authorityList,
    ministryParameters,
  }
}
